// compras.service.ts
//
// O caminho pelo qual as telas de Compras leem a compra automática (§10.5):
// necessidades detectadas, ordens consolidadas, alertas e o log de mensagem.
//
// MASCARAMENTO: preço unitário, subtotal, valor total, teto de linha e pedido
// mínimo podem NÃO VIR na resposta, conforme o papel de quem pediu. Isso é
// decidido no servidor — a coluna sai de fora do JSON, não fica escondida na
// tela. **Uma chave ausente aqui não é erro**: é a resposta certa para quem não
// alcança dinheiro, e a tela mostra "—" em vez de "R$ undefined". A resposta
// traz `omitido` com a lista do que ficou de fora, para a tela poder dizer por
// que o número não está lá.
//
// O QUE ESTE SERVIÇO NÃO FAZ: enviar mensagem para fornecedor. Aprovar apenas
// abre a janela de arrependimento de 15 minutos; `executar` roda o motor com o
// adaptador de canal em modo SIMULAÇÃO, que grava o texto exato e não chama
// canal nenhum.

import api from "./api.service";

export interface Paginacao {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface RespostaPaginada<T = any> {
  dados: T[];
  paginacao: Paginacao;
  /** Colunas que o servidor NÃO devolveu por permissão (§4). */
  omitido: string[];
}

export interface Necessidade {
  necessidadeUuid: string;
  subsidiary: string;
  lojaNome?: string | null;
  skuUuid: string;
  produto?: string | null;
  fornecedor: string | null;
  fornecedorNome?: string | null;
  quantidade: number;
  unidadeCompra: string | null;
  /** Ausente quando o papel não alcança dinheiro. */
  precoUnitario?: number | null;
  semPreco: boolean;
  saldoNoDisparo: number;
  pontoNoDisparo: number;
  consumoMedioDiario: number | null;
  urgente: boolean;
  origemDaRegra: string;
  estado: string;
  motivoDoEstado: string | null;
  detectadaEm: string;
  encerradaEm: string | null;
}

export interface Ordem {
  ordemUuid: string;
  codigo: string;
  subsidiary: string;
  lojaNome?: string | null;
  fornecedor: string;
  fornecedorNome: string;
  estado: string;
  modoDeDisparo: string;
  motivoDoModo: string | null;
  valorTotal?: number;
  quantidadeDeItens: number;
  pedidoMinimoNoMomento?: number | null;
  faltaParaMinimo?: number | null;
  faltaParaFreteGratis?: number | null;
  linhas: number;
  linhasAcimaDoTeto: number;
  linhasSemPreco: number;
  aprovadoPor: string | null;
  aprovadoEm: string | null;
  liberadaEm: string | null;
  envioSimuladoEm: string | null;
  enviadaEm: string | null;
  motivoDeBloqueio: string | null;
  createdAt: string;
}

export interface OrdemItem {
  ordemItemUuid: string;
  skuUuid: string;
  produto?: string | null;
  quantidade: number;
  unidadeCompra: string;
  precoUnitario?: number | null;
  subtotal?: number | null;
  semPreco: boolean;
  tetoDeLinhaNoMomento?: number | null;
  estouraTetoDeLinha: boolean;
  origem: string;
  saldoNoDisparo: number | null;
  pontoNoDisparo: number | null;
}

export interface MensagemDeSaida {
  mensagemUuid: string;
  codigoDaOrdem: string | null;
  proposito: string;
  canal: string;
  destinatario: string;
  destinatarioTipo: string;
  template: string;
  textoExato: string;
  estado: string;
  erro: string | null;
  criadaEm: string;
  enviadaEm: string | null;
}

export interface AlertaDeCompra {
  alertaUuid: string;
  tipo: string;
  severidade: string;
  titulo: string;
  detalhe: string | null;
  dados: any;
  estado: string;
  createdAt: string;
}

export interface DetalheDaOrdem {
  ordem: Ordem;
  itens: OrdemItem[];
  mensagens: MensagemDeSaida[];
  alertas: AlertaDeCompra[];
  aprovacoes: any[];
  omitido: string[];
}

export interface FiltroDeCompras {
  page?: number;
  limit?: number;
  subsidiary?: string;
  fornecedor?: string;
  estado?: string;
  busca?: string;
  ordem?: string;
}

/** Só o que foi preenchido vai na query — `busca=` vazio faria um ILIKE '%%'. */
function comoQuery(filtro: FiltroDeCompras): string {
  const parametros = new URLSearchParams();
  if (filtro.page) parametros.set("page", String(filtro.page));
  if (filtro.limit) parametros.set("limit", String(filtro.limit));
  if (filtro.subsidiary) parametros.set("subsidiary", filtro.subsidiary);
  if (filtro.fornecedor) parametros.set("fornecedor", filtro.fornecedor);
  if (filtro.estado) parametros.set("estado", filtro.estado);
  if (filtro.busca) parametros.set("busca", filtro.busca);
  if (filtro.ordem) parametros.set("ordem", filtro.ordem);
  return parametros.toString();
}

const comprasService = {
  async necessidades(
    filtro: FiltroDeCompras = {},
  ): Promise<RespostaPaginada<Necessidade>> {
    const { data } = await api.get(
      `suprimentos/compras/necessidades?${comoQuery(filtro)}`,
    );
    return data;
  },

  async ordens(filtro: FiltroDeCompras = {}): Promise<RespostaPaginada<Ordem>> {
    const { data } = await api.get(`suprimentos/compras/ordens?${comoQuery(filtro)}`);
    return data;
  },

  async ordem(uuid: string): Promise<DetalheDaOrdem> {
    const { data } = await api.get(`suprimentos/compras/ordens/${uuid}`);
    return data;
  },

  async alertas(
    filtro: FiltroDeCompras = {},
  ): Promise<RespostaPaginada<AlertaDeCompra>> {
    const { data } = await api.get(`suprimentos/compras/alertas?${comoQuery(filtro)}`);
    return data;
  },

  async mensagens(
    filtro: FiltroDeCompras = {},
  ): Promise<
    RespostaPaginada<MensagemDeSaida> & { enviadasParaFornecedorReal: number }
  > {
    const { data } = await api.get(
      `suprimentos/compras/mensagens?${comoQuery(filtro)}`,
    );
    return data;
  },

  // `updatedBy` é obrigatório no corpo: `request.userUuid` nunca é populado no
  // backend, e sem ele a gravação morre numa coluna NOT NULL.
  async aprovar(uuid: string, updatedBy: string) {
    const { data } = await api.post(
      `suprimentos/compras/ordens/${uuid}/aprovar`,
      { updatedBy },
    );
    return data;
  },

  async recusar(uuid: string, updatedBy: string, motivo: string) {
    const { data } = await api.post(
      `suprimentos/compras/ordens/${uuid}/recusar`,
      { updatedBy, motivo },
    );
    return data;
  },

  async cancelar(uuid: string, updatedBy: string, motivo: string) {
    const { data } = await api.post(
      `suprimentos/compras/ordens/${uuid}/cancelar`,
      { updatedBy, motivo },
    );
    return data;
  },

  /** Disparo manual. Sem `aplicar: true` é simulação e não grava nada. */
  async executar(corpo: {
    fase?: "1" | "2" | "ambas";
    subsidiary?: string;
    aplicar?: boolean;
    updatedBy?: string;
  }) {
    const { data } = await api.post(`suprimentos/compras/executar`, corpo);
    return data;
  },
};

/**
 * Dinheiro para a tela. `undefined` NÃO vira "R$ 0,00": vira travessão, porque
 * a chave ausente significa "sua permissão não alcança este número", e um zero
 * ali diria que o pedido não custa nada.
 */
export function comoReais(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return "—";
  return valor.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function comoQuantidade(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return "—";
  return valor.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

export function comoDataHora(valor: string | null | undefined): string {
  if (!valor) return "—";
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return "—";
  return data.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/**
 * O `motivoDoModo` que o servidor grava é uma lista de etiquetas separadas por
 * vírgula. A tela precisa DIZER, em texto, por que a ordem está em SUGERIR —
 * uma etiqueta crua ("tetoDeLinha") não explica nada para o dono.
 */
export const EXPLICACAO_DO_MODO: Record<string, string> = {
  padraoDeFabrica:
    "o padrão de fábrica é SUGERIR: envio automático é opt-in por SKU, e nenhum limite desta ordem foi marcado como 'enviar'",
  tetoDeLinha:
    "pelo menos uma linha passou do teto configurado NAQUELE limite (o teto é por linha, não pelo total do carrinho)",
  tetoDeOrdem: "o total da ordem passou do teto de ordem do fornecedor",
  semPreco:
    "pelo menos uma linha está sem preço acordado vigente — uma ordem sem preço não pode sair sozinha",
  alcada: "o valor está acima da maior alçada cadastrada para esta loja",
  envioRealDesligado:
    "o envio real está desligado no servidor (COMPRA_ENVIO_REAL ausente): o canal roda em modo simulação e nada sai para o fornecedor",
};

export function explicarModo(motivoDoModo: string | null): string[] {
  if (!motivoDoModo) return [];
  return motivoDoModo
    .split(",")
    .map((etiqueta) => etiqueta.trim())
    .filter(Boolean)
    .map((etiqueta) => EXPLICACAO_DO_MODO[etiqueta] ?? etiqueta);
}

export default comprasService;
